// Global Instructions Rule Applied!
//
// Operations → AGENTS → Agent Oversight (admin only).
//
// The cross-user oversight screen for the agent gateway: what agents have
// been doing over a period, every agent write with the owner's verbs, and who
// has connected which agent with what rights.
//
// One PERIOD (UTC days — the unit usage is counted and capped in) drives the
// history sections. It sits in the first section's header, the way the Runs
// log keeps its filters inside its own section.
//
//   1. Usage            — KPI tiles over the period, and per connection: the
//                         period's calls / rows / writes beside TODAY's usage
//                         vs the caps (the caps reset daily, so they stay a
//                         today figure). view__agent_usage_admin +
//                         view__agent_access_admin.usage.
//   2. Usage by day     — every UTC day in the period, with an inline bar.
//      Usage by tool    — busiest first, share-of-calls bar.
//   3. Activity         — every agent_proposals row in the period across
//                         users, filtered on the server (status group,
//                         connection, tool, live/proposal, text) and windowed
//                         by infinite scroll (view__agent_proposals_admin).
//                         Same verbs as the owner's My Agents (owner decision
//                         2026-09-02): Review · Reject, Review & retry,
//                         Open · Undo — the RPCs decide who may act.
//   4. Tool health, Failed calls | Searches with no results, Arguments
//      agents use | Tools not granted, Call log — AgentCallLogSections, over
//      public.agent_call_log (every MCP tool call, refusals included).
//   5. Connections      — every user's grants (rights, granted / expires,
//                         status) plus calls in the period and the last call.
//                         VIEW-ONLY: grants are self-issued and self-revoked;
//                         the admin's levers stay the product kill switch and
//                         caps on the Agent Gateway panel.
//
// Rows, rendering and verbs for proposals are the owner screen's
// (agent_access/utils/proposalPresentation + useProposalActions). Chrome:
// FormSections with antd Tables inside, the Runs log's filter row and
// infinite-scroll idiom (FollowerAgentsPanel).
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Progress, Segmented, Select, Table, Tag, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { KpiTile } from '../../../core/components/KpiTile.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import {
  AgentAccessAdminConnection,
  AgentAccessAdminUsageRow,
  AgentActivityQuery,
  AgentActivityStatusGroup,
  AgentProposalAdmin,
  AgentUsageAdmin,
  AgentUsageDay,
  AgentUsageGrant,
  AgentUsageTool
} from '../../../state_manager/data_models/agent_access_admin_model.ts'
import {
  fetchAgentAccessAdmin,
  fetchAgentProposalsAdmin,
  fetchAgentUsageAdmin
} from '../services/agentAccessAdminService.ts'
import { useProposalActions } from '../../agent_access/hooks/useProposalActions.ts'
import {
  ProposalActions,
  ProposalPayloadPanel,
  editedTag,
  outcomeLines,
  statusTag
} from '../../agent_access/utils/proposalPresentation.tsx'
import { payloadsEqual } from '../../agent_access/utils/proposalForms.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'
import AgentCallLogSections from './AgentCallLogSections.tsx'
import {
  PERIOD_PRESETS,
  Period,
  PeriodPreset,
  periodLabel,
  periodTimestamps,
  presetPeriod,
  windowLabel
} from '../utils/agentOversightPeriod.ts'

const { Text } = Typography

// LAZY, and mounted only while a review is open. The drawer renders the real
// New forms, so importing it eagerly would pull the entire deal / company /
// contact / prospect / interaction form stack into the Configuration bundle.
const ProposalReviewDrawer = lazy(() =>
  import('../../agent_access/components/ProposalReviewDrawer.tsx').then((m) => ({
    default: m.ProposalReviewDrawer
  }))
)

// Activity window: one server round-trip per scroll-to-bottom.
const ACTIVITY_PAGE_SIZE = 100
const SCROLL_THRESHOLD_PX = 300
const ACTIVITY_BODY_HEIGHT = 'calc(100vh - 320px)'
const SEARCH_DEBOUNCE_MS = 300

type StatusFilter = 'All' | 'Pending' | 'Applied' | 'Failed' | 'Other'
const STATUS_GROUP: Record<StatusFilter, AgentActivityStatusGroup | undefined> = {
  All: undefined,
  Pending: 'pending',
  Applied: 'applied',
  Failed: 'failed',
  Other: 'other'
}

const compactNumber = (v?: number | null): string => Number(v ?? 0).toLocaleString()

// Admin grids use the house MM/DD/YYYY.
const formatDate = (value?: string | null): string => (value ? dayjs(value).format('MM/DD/YYYY') : '—')
const formatDateTime = (value?: string | null): string =>
  value ? dayjs(value).format('MM/DD/YYYY HH:mm') : '—'

const grantStatusTag = (status: string): React.ReactElement =>
  status === 'active' ? <Tag color='green'>Active</Tag> : status === 'expired' ? <Tag>Expired</Tag> : <Tag>Revoked</Tag>

// Consumption vs a cap as a small progress bar; caps of 0 mean uncapped and
// get words instead of a misleading full/empty bar ("uncapped", lowercase —
// the exact wording the gateway panel uses).
const capProgress = (used: number, cap: number, unit: string): React.ReactElement => {
  if (!cap || cap <= 0) {
    return (
      <Text type='secondary' style={{ fontSize: 12 }}>
        {compactNumber(used)} · uncapped
      </Text>
    )
  }
  const pct = Math.round((used / cap) * 100)
  return (
    <span className='flex items-center gap-2'>
      <Progress
        size='small'
        percent={Math.min(pct, 100)}
        showInfo={false}
        status={pct >= 100 ? 'exception' : 'normal'}
        strokeColor={pct >= 100 ? undefined : pct >= 80 ? '#faad14' : undefined}
        style={{ width: 90, margin: 0 }}
      />
      <Text type='secondary' style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {compactNumber(used)} / {compactNumber(cap)} {unit}
      </Text>
    </span>
  )
}

// A value as a share of the column's largest, drawn the same way as the caps
// (the owner chose tables with inline bars over a chart library, 2026-09-18).
const shareBar = (value: number, max: number): React.ReactElement => (
  <span className='flex items-center gap-2'>
    <Progress
      size='small'
      percent={max > 0 ? Math.round((value / max) * 100) : 0}
      showInfo={false}
      // A share is not a completion: without this antd turns the column's
      // largest (100%) bar green, reading as "done".
      status='normal'
      style={{ width: 110, margin: 0 }}
    />
    <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{compactNumber(value)}</Text>
  </span>
)

const connectionLabel = (user: string | null, agent: string): string => `${user || 'Unknown user'} · ${agent}`

const EMPTY_USAGE: AgentUsageAdmin = {
  period: { prog__date_from: '', prog__date_to: '' },
  totals: {
    prog__calls: 0,
    prog__rows_returned: 0,
    prog__active_connections: 0,
    prog__active_users: 0,
    prog__writes: 0,
    prog__failed_writes: 0
  },
  by_day: [],
  by_grant: [],
  by_tool: []
}

export default function AgentAccessAdminPanel(): React.ReactElement {
  const token = () => getAccessToken(store.getState().auth)
  const { openFormInTab } = useTabManagerContext()

  // ── period ─────────────────────────────────────────────────────────────
  const [preset, setPreset] = useState<PeriodPreset>('7d')
  const [customRange, setCustomRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const period: Period = useMemo(() => {
    if (preset === 'custom' && customRange?.[0] && customRange?.[1]) {
      return { date_from: customRange[0].format('YYYY-MM-DD'), date_to: customRange[1].format('YYYY-MM-DD') }
    }
    const days = PERIOD_PRESETS.find((p) => p.value === preset)?.days ?? 7
    return presetPeriod(days)
  }, [preset, customRange])

  // Choosing Custom starts the picker at the period already on screen, so the
  // data does not jump to some other range before the admin has picked one.
  const choosePreset = (next: PeriodPreset) => {
    if (next === 'custom' && !(customRange?.[0] && customRange?.[1])) {
      setCustomRange([dayjs(period.date_from), dayjs(period.date_to)])
    }
    setPreset(next)
  }

  // ── right now: connections + today's usage vs caps ─────────────────────
  const [connections, setConnections] = useState<AgentAccessAdminConnection[]>([])
  const [todayUsage, setTodayUsage] = useState<AgentAccessAdminUsageRow[]>([])
  const [nowLoading, setNowLoading] = useState(true)

  const loadNow = useCallback(async () => {
    setNowLoading(true)
    try {
      // The activity list has its own read now; ask the aggregate for one row.
      const data = await fetchAgentAccessAdmin(token(), 1)
      setConnections(data.connections)
      setTodayUsage(data.usage)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load agent connections')
    } finally {
      setNowLoading(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── usage over the period ──────────────────────────────────────────────
  const [usage, setUsage] = useState<AgentUsageAdmin>(EMPTY_USAGE)
  const [usageLoading, setUsageLoading] = useState(true)

  const loadUsage = useCallback(async () => {
    setUsageLoading(true)
    try {
      setUsage(await fetchAgentUsageAdmin(token(), period))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load agent usage')
    } finally {
      setUsageLoading(false)
    }
  }, [period]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── activity (server-filtered, windowed) ───────────────────────────────
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All')
  const [grantFilter, setGrantFilter] = useState<string | null>(null)
  const [toolFilter, setToolFilter] = useState<string | null>(null)
  const [modeFilter, setModeFilter] = useState<'live' | 'proposal' | null>(null)
  const [searchText, setSearchText] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchText.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchText])

  const [activity, setActivity] = useState<AgentProposalAdmin[]>([])
  const [activityCount, setActivityCount] = useState(0)
  const [activityHasMore, setActivityHasMore] = useState(false)
  const [activityLoading, setActivityLoading] = useState(true)
  const [activityLoadingMore, setActivityLoadingMore] = useState(false)
  const activityTableRef = useRef<HTMLDivElement | null>(null)

  const activityQuery = useCallback(
    (offset: number): AgentActivityQuery => ({
      status: STATUS_GROUP[statusFilter],
      grant_id: grantFilter ?? undefined,
      tool: toolFilter ?? undefined,
      live: modeFilter === null ? undefined : modeFilter === 'live',
      search: search || undefined,
      ...periodTimestamps(period),
      limit: ACTIVITY_PAGE_SIZE,
      offset
    }),
    [statusFilter, grantFilter, toolFilter, modeFilter, search, period]
  )

  const loadActivity = useCallback(async () => {
    setActivityLoading(true)
    try {
      const page = await fetchAgentProposalsAdmin(token(), activityQuery(0))
      setActivity(page.data)
      setActivityHasMore(page.has_more)
      setActivityCount(page.count)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load agent activity')
      setActivity([])
      setActivityHasMore(false)
    } finally {
      setActivityLoading(false)
    }
  }, [activityQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreActivity = useCallback(async () => {
    if (activityLoading || activityLoadingMore || !activityHasMore) return
    setActivityLoadingMore(true)
    try {
      const page = await fetchAgentProposalsAdmin(token(), activityQuery(activity.length))
      setActivity((prev) => [...prev, ...page.data])
      setActivityHasMore(page.has_more)
      setActivityCount(page.count)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load more agent activity')
      setActivityHasMore(false)
    } finally {
      setActivityLoadingMore(false)
    }
  }, [activityQuery, activity.length, activityLoading, activityLoadingMore, activityHasMore]) // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll on the table body (the Runs log idiom): the body is the
  // only thing that grows, so the sections below never get pushed around.
  useEffect(() => {
    const body = activityTableRef.current?.querySelector('.ant-table-body') as HTMLElement | null
    if (!body) return
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = body
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) loadMoreActivity()
    }
    body.addEventListener('scroll', onScroll)
    return () => body.removeEventListener('scroll', onScroll)
  }, [loadMoreActivity])

  useEffect(() => {
    loadNow()
  }, [loadNow])
  useEffect(() => {
    loadUsage()
  }, [loadUsage])
  useEffect(() => {
    loadActivity()
  }, [loadActivity])

  // The call-log sections own their reads; bumping this re-runs them.
  const [callLogRefreshKey, setCallLogRefreshKey] = useState(0)

  const refreshAll = useCallback(async () => {
    setCallLogRefreshKey((k) => k + 1)
    await Promise.all([loadNow(), loadUsage(), loadActivity()])
  }, [loadNow, loadUsage, loadActivity])

  // ── the owner screen's verbs against this list ─────────────────────────
  const { reviewingId, review, retry, undo } = useProposalActions(refreshAll)
  const [reviewing, setReviewing] = useState<AgentProposalAdmin | null>(null)

  // Identical to the owner tab's: the edited payload is sent ONLY when it
  // differs from what the agent filed, so an untouched proposal does not
  // acquire an original_payload and start reading as amended.
  const handleApprove = useCallback(
    async (payload: Record<string, any>) => {
      if (!reviewing) return
      const filed = (reviewing.agent_proposals__payload ?? {}) as Record<string, unknown>
      const edited = payloadsEqual(payload, filed) ? null : payload
      const ok =
        reviewing.agent_proposals__status === 'failed'
          ? await retry(reviewing.agent_proposals__id, edited)
          : await review(reviewing.agent_proposals__id, 'approve', edited)
      if (ok) setReviewing(null)
    },
    [reviewing, retry, review]
  )

  const handleReject = useCallback(async () => {
    if (!reviewing) return
    const ok = await review(reviewing.agent_proposals__id, 'reject')
    if (ok) setReviewing(null)
  }, [reviewing, review])

  // ── derived lookups ────────────────────────────────────────────────────
  const periodByGrant = useMemo(
    () => new Map(usage.by_grant.map((g) => [g.agent_grants__id, g] as const)),
    [usage.by_grant]
  )
  const todayByGrant = useMemo(
    () => new Map(todayUsage.map((u) => [u.agent_grants__id, u] as const)),
    [todayUsage]
  )
  const grantOptions = useMemo(
    () =>
      connections.map((c) => ({
        value: c.agent_grants__id,
        label: connectionLabel(c.prog__user_name, c.prog__agent_name)
      })),
    [connections]
  )
  const toolOptions = useMemo(() => {
    const tools = new Set<string>()
    connections.forEach((c) => (c.agent_grants__scopes?.tools || []).forEach((t) => tools.add(t)))
    usage.by_tool.forEach((t) => tools.add(t.prog__tool_key))
    return Array.from(tools)
      .sort()
      .map((t) => ({ value: t, label: t }))
  }, [connections, usage.by_tool])

  const maxDayCalls = useMemo(() => Math.max(0, ...usage.by_day.map((d) => d.prog__calls)), [usage.by_day])
  const maxToolCalls = useMemo(() => Math.max(0, ...usage.by_tool.map((t) => t.prog__calls)), [usage.by_tool])
  const maxGrantCalls = useMemo(() => Math.max(0, ...usage.by_grant.map((g) => g.prog__calls)), [usage.by_grant])

  // ── columns ────────────────────────────────────────────────────────────
  const usageColumns = [
    {
      title: 'Connection',
      key: 'connection',
      render: (_: unknown, row: AgentUsageGrant) => (
        <>
          <Text>{row.prog__user_name || 'Unknown user'}</Text>
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.prog__agent_name}
          </Text>
        </>
      )
    },
    { title: 'Status', dataIndex: 'prog__grant_status', key: 'status', width: 90, render: grantStatusTag },
    {
      title: 'Calls',
      key: 'calls',
      width: 170,
      render: (_: unknown, row: AgentUsageGrant) => shareBar(row.prog__calls, maxGrantCalls)
    },
    {
      title: 'Rows',
      dataIndex: 'prog__rows_returned',
      key: 'rows',
      width: 90,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    {
      title: 'Writes',
      key: 'writes',
      width: 110,
      render: (_: unknown, row: AgentUsageGrant) => (
        <span className='whitespace-nowrap'>
          {compactNumber(row.prog__writes)}
          {row.prog__failed_writes ? (
            <Tag color='red' style={{ marginLeft: 6 }}>
              {row.prog__failed_writes} failed
            </Tag>
          ) : null}
        </span>
      )
    },
    {
      title: 'Peak / min today',
      key: 'peak',
      width: 190,
      render: (_: unknown, row: AgentUsageGrant) => {
        const today = todayByGrant.get(row.agent_grants__id)
        return today ? capProgress(today.prog__peak_requests_per_minute, today.agents__requests_per_minute, 'per min') : '—'
      }
    },
    {
      title: 'Rows today',
      key: 'rows_today',
      width: 200,
      render: (_: unknown, row: AgentUsageGrant) => {
        const today = todayByGrant.get(row.agent_grants__id)
        return today ? capProgress(today.prog__rows_returned_today, today.agents__daily_row_limit, 'rows') : '—'
      }
    },
    {
      title: 'Write budget today',
      key: 'write_budget',
      width: 220,
      render: (_: unknown, row: AgentUsageGrant) => {
        const caps = todayByGrant.get(row.agent_grants__id)?.prog__write_caps || []
        return caps.length ? (
          <>
            {caps.map((cap) => (
              <div key={cap.tool_key} className='flex items-center gap-2'>
                <Text type='secondary' style={{ fontSize: 12, minWidth: 90 }}>
                  {cap.tool_key}
                </Text>
                {capProgress(cap.prog__created_today, cap.prog__max_per_day, '/ day')}
              </div>
            ))}
          </>
        ) : (
          <Text type='secondary'>—</Text>
        )
      }
    }
  ]

  const dayColumns = [
    { title: 'Day (UTC)', dataIndex: 'prog__day', key: 'day', width: 110, render: (v: string) => formatDate(v) },
    {
      title: 'Calls',
      key: 'calls',
      render: (_: unknown, row: AgentUsageDay) => shareBar(row.prog__calls, maxDayCalls)
    },
    {
      title: 'Rows',
      dataIndex: 'prog__rows_returned',
      key: 'rows',
      width: 90,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    {
      title: 'Writes',
      key: 'writes',
      width: 110,
      render: (_: unknown, row: AgentUsageDay) => (
        <span className='whitespace-nowrap'>
          {compactNumber(row.prog__writes)}
          {row.prog__failed_writes ? (
            <Tag color='red' style={{ marginLeft: 6 }}>
              {row.prog__failed_writes} failed
            </Tag>
          ) : null}
        </span>
      )
    }
  ]

  const toolColumns = [
    {
      title: 'Tool',
      key: 'tool',
      width: 190,
      render: (_: unknown, row: AgentUsageTool) => (
        <span className='whitespace-nowrap'>
          <Text>{row.prog__tool_key}</Text>
          {row.prog__tool_kind === 'write' ? (
            <Tag color='purple' style={{ marginLeft: 6 }}>
              write
            </Tag>
          ) : null}
        </span>
      )
    },
    {
      title: 'Calls',
      key: 'calls',
      width: 170,
      render: (_: unknown, row: AgentUsageTool) => shareBar(row.prog__calls, maxToolCalls)
    },
    {
      title: 'Rows',
      dataIndex: 'prog__rows_returned',
      key: 'rows',
      width: 80,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    {
      title: 'Connections',
      dataIndex: 'prog__connections',
      key: 'connections',
      width: 120,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    {
      title: 'Last call',
      dataIndex: 'prog__last_call_at',
      key: 'last',
      width: 140,
      render: (v: string | null) => formatDateTime(v)
    }
  ]

  const activityColumns = [
    {
      title: 'User',
      dataIndex: 'prog__user_name',
      key: 'user',
      width: 160,
      render: (v: string | null, row: AgentProposalAdmin) => (
        <Text>{row.prog__user_is_me ? 'You' : v || 'Unknown user'}</Text>
      )
    },
    {
      title: 'Proposal',
      key: 'proposal',
      render: (_: unknown, row: AgentProposalAdmin) => (
        <>
          <Text strong>{row.agent_proposals__summary || row.agent_proposals__tool}</Text>
          {editedTag(row)}
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.agent_proposals__agent_name} · {row.agent_proposals__tool}
          </Text>
          {row.agent_proposals__rationale ? (
            <>
              <br />
              <Text type='secondary' style={{ fontSize: 12 }}>
                “{row.agent_proposals__rationale}”
              </Text>
            </>
          ) : null}
        </>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      render: (_: unknown, row: AgentProposalAdmin) => (
        <>
          {statusTag(row)}
          {outcomeLines(row).map((line, index) => (
            <React.Fragment key={index}>
              <br />
              <Text type={line.type} style={{ fontSize: 12 }}>
                {line.text}
              </Text>
            </React.Fragment>
          ))}
        </>
      )
    },
    {
      title: 'Filed',
      dataIndex: 'agent_proposals__date_created',
      key: 'filed',
      width: 110,
      render: formatDate
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_: unknown, row: AgentProposalAdmin) => (
        <ProposalActions
          proposal={row}
          busy={reviewingId === row.agent_proposals__id}
          onReview={(proposal) => setReviewing(proposal as AgentProposalAdmin)}
          onReject={(id) => review(id, 'reject')}
          onUndo={undo}
          onOpen={(target) => openFormInTab(target.module, target.id, null, target.query ?? null)}
        />
      )
    }
  ]

  const connectionColumns = [
    {
      title: 'User',
      dataIndex: 'prog__user_name',
      key: 'user',
      render: (v: string | null) => <Text>{v || 'Unknown user'}</Text>
    },
    {
      title: 'Agent',
      key: 'agent',
      render: (_: unknown, row: AgentAccessAdminConnection) => (
        <>
          <Text>{row.prog__agent_name}</Text>
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.agent_grants__agent_id}
          </Text>
        </>
      )
    },
    {
      title: 'Authorised tools',
      key: 'tools',
      render: (_: unknown, row: AgentAccessAdminConnection) => {
        const tools = row.agent_grants__scopes?.tools || []
        const liveTools = new Set(row.agent_grants__scopes?.live_tools || [])
        if (!tools.length) return <Text type='secondary'>—</Text>
        return (
          <>
            {tools.map((tool) =>
              liveTools.has(tool) ? (
                // Live-consented write tools are the elevated rights — make
                // them visually distinct from plain reads/proposals.
                <Tag key={tool} color='purple' style={{ marginBottom: 2 }}>
                  {tool} · live
                </Tag>
              ) : (
                <Tag key={tool} style={{ marginBottom: 2 }}>
                  {tool}
                </Tag>
              )
            )}
          </>
        )
      }
    },
    {
      title: 'Calls in period',
      key: 'calls',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, row: AgentAccessAdminConnection) =>
        compactNumber(periodByGrant.get(row.agent_grants__id)?.prog__calls ?? 0)
    },
    {
      title: 'Last call',
      key: 'last_call',
      width: 140,
      render: (_: unknown, row: AgentAccessAdminConnection) =>
        formatDateTime(periodByGrant.get(row.agent_grants__id)?.prog__last_call_at ?? null)
    },
    { title: 'Granted', dataIndex: 'agent_grants__date_created', key: 'granted', width: 110, render: formatDate },
    { title: 'Expires', dataIndex: 'agent_grants__expires_at', key: 'expires', width: 110, render: formatDate },
    { title: 'Status', dataIndex: 'prog__grant_status', key: 'status', width: 100, render: grantStatusTag }
  ]

  const activeCount = useMemo(
    () => connections.filter((c) => c.prog__grant_status === 'active').length,
    [connections]
  )
  const totals = usage.totals

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      <FormSection
        title='Usage'
        titleColor='green'
        scrollable={false}
        loading={usageLoading && !usage.period.prog__date_from}
        titleSuffix={
          <Text type='secondary' style={{ fontSize: 12, marginLeft: 8 }}>
            {periodLabel(period)}
          </Text>
        }
        headerActions={
          <span className='flex items-center gap-2'>
            <Segmented
              size='small'
              options={PERIOD_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
              value={preset}
              onChange={(value) => choosePreset(value as PeriodPreset)}
            />
            {preset === 'custom' ? (
              <DatePicker.RangePicker
                size='small'
                value={customRange}
                onChange={(value) => setCustomRange(value)}
                format='MM/DD/YYYY'
                disabledDate={(d) => d.isAfter(dayjs(), 'day')}
              />
            ) : null}
            <Button size='small' icon={<ReloadOutlined />} onClick={refreshAll}>
              Refresh
            </Button>
          </span>
        }
      >
        <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3'>
          <KpiTile title='Tool calls' value={compactNumber(totals.prog__calls)} loading={usageLoading} />
          <KpiTile title='Rows returned' value={compactNumber(totals.prog__rows_returned)} loading={usageLoading} />
          <KpiTile title='Active connections' value={compactNumber(totals.prog__active_connections)} loading={usageLoading} />
          <KpiTile title='Active users' value={compactNumber(totals.prog__active_users)} loading={usageLoading} />
          <KpiTile title='Writes' value={compactNumber(totals.prog__writes)} loading={usageLoading} />
          <KpiTile
            title='Failed writes'
            value={compactNumber(totals.prog__failed_writes)}
            loading={usageLoading}
            valueStyle={totals.prog__failed_writes ? { color: '#ff4d4f' } : undefined}
          />
        </div>
        <Text type='secondary' style={{ fontSize: 12 }}>
          Calls, rows and writes are for the period; the “today” columns are since 00:00 UTC against the caps set on
          the Agent Gateway panel.
        </Text>
        <Table
          className='mt-2'
          size='small'
          rowKey='agent_grants__id'
          pagination={false}
          dataSource={usage.by_grant}
          columns={usageColumns}
          scroll={{ x: 1250 }}
          locale={{ emptyText: 'No agent connections yet' }}
        />
      </FormSection>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <FormSection title='Usage by day' titleColor='blue' scrollable={false} loading={usageLoading}>
          <Table
            size='small'
            rowKey='prog__day'
            pagination={false}
            dataSource={usage.by_day}
            columns={dayColumns}
            scroll={{ y: 420 }}
            locale={{ emptyText: 'No days in this period' }}
          />
        </FormSection>
        <FormSection title='Usage by tool' titleColor='blue' scrollable={false} loading={usageLoading}>
          <Table
            size='small'
            rowKey='prog__tool_key'
            pagination={false}
            dataSource={usage.by_tool}
            columns={toolColumns}
            scroll={{ x: 720, y: 420 }}
            locale={{ emptyText: 'No tool calls in this period' }}
          />
        </FormSection>
      </div>

      <FormSection
        title='Activity'
        titleColor='gray'
        scrollable={false}
        loading={activityLoading && activity.length === 0}
        headerActions={
          <Text type='secondary' style={{ fontSize: 12 }}>
            Agent writes in the period across all users — pending first, then newest
          </Text>
        }
      >
        {/* Filters sit in a body row, not headerActions (the Runs log idiom):
            five controls do not fit the header slot. All go to the server. */}
        <div className='flex items-center gap-2 flex-wrap mb-2'>
          <Segmented
            size='small'
            options={['All', 'Pending', 'Applied', 'Failed', 'Other']}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as StatusFilter)}
          />
          <Select
            size='small'
            className='w-64'
            allowClear
            showSearch
            optionFilterProp='label'
            placeholder='All connections'
            value={grantFilter}
            onChange={(value) => setGrantFilter(value ?? null)}
            options={grantOptions}
          />
          <Select
            size='small'
            className='w-44'
            allowClear
            showSearch
            placeholder='All tools'
            value={toolFilter}
            onChange={(value) => setToolFilter(value ?? null)}
            options={toolOptions}
          />
          <Select
            size='small'
            className='w-36'
            allowClear
            placeholder='Live or proposal'
            value={modeFilter}
            onChange={(value) => setModeFilter(value ?? null)}
            options={[
              { value: 'live', label: 'Live writes' },
              { value: 'proposal', label: 'Proposals' }
            ]}
          />
          <Input
            size='small'
            className='w-56'
            allowClear
            placeholder='Summary contains…'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div ref={activityTableRef}>
          <Table
            size='small'
            rowKey='agent_proposals__id'
            pagination={false}
            dataSource={activity}
            columns={activityColumns}
            scroll={{ x: 1000, y: ACTIVITY_BODY_HEIGHT }}
            expandable={{
              // The exact payload an approval would replay, shown verbatim —
              // the same panel the owner sees.
              expandedRowRender: (row: AgentProposalAdmin) => <ProposalPayloadPanel proposal={row} />,
              rowExpandable: () => true
            }}
            locale={{ emptyText: activityLoading ? ' ' : 'No agent writes match these filters' }}
          />
        </div>
        <div className='h-8 flex items-center text-xs text-gray-400'>
          {activityLoadingMore ? 'Loading more…' : windowLabel(activity.length, activityCount, 'entry', 'entries')}
        </div>
      </FormSection>

      <AgentCallLogSections period={period} grantOptions={grantOptions} refreshKey={callLogRefreshKey} />

      <FormSection
        title='Connections'
        titleColor='blue'
        scrollable={false}
        loading={nowLoading}
        headerActions={
          <Text type='secondary' style={{ fontSize: 12 }}>
            {activeCount} active — grants are edited and revoked by their owner
          </Text>
        }
      >
        <Table
          size='small'
          rowKey='agent_grants__id'
          pagination={false}
          dataSource={connections}
          columns={connectionColumns}
          scroll={{ x: 1200 }}
          locale={{ emptyText: 'No agent connections yet' }}
        />
      </FormSection>

      {/* The owner screen's review drawer. onBehalfOf keeps an admin honest
          about whose proposal they are approving — null on their own rows.
          Mounted only while a review is open (see the lazy import above). */}
      {reviewing ? (
        <Suspense fallback={null}>
          <ProposalReviewDrawer
            proposal={reviewing}
            busy={reviewingId === reviewing.agent_proposals__id}
            onApprove={handleApprove}
            onReject={handleReject}
            onClose={() => setReviewing(null)}
            onOpenRecord={openFormInTab}
            onBehalfOf={reviewing.prog__user_is_me ? null : reviewing.prog__user_name}
          />
        </Suspense>
      ) : null}
    </div>
  )
}
